//@flow
import React from 'react';
import {useLocation} from 'react-router-dom';

import {
  makeStyles,
  Typography,
  Chip,
  CircularProgress,
} from '@material-ui/core';
import IosSwitch from '../../elements/IosSwitch/IosSwitch';
import {colors} from '../../assets/styles/colors';
import {classNames} from '../../utils/react';

type Props = {
  id?: string,
  title: string,
  subtitle?: string,
  enabled?: boolean,
  chipLabel?: string | React$Node,
  loading?: boolean,
  children: React$Node,
  onToggleEnable?: (checked: boolean) => void,
  classes?: {
    root?: string,
    content?: string,
  },
};

const useStyles = makeStyles(theme => ({
  root: {
    background: 'white',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    borderRadius: 12,
    padding: '12px 16px 16px',
    position: 'relative',

    '&[data-highlighted="true"]': {
      animation: `$highlightedEffect 4000ms ${theme.transitions.easing.easeInOut}`,
    },
    scrollMarginTop: '80px',
    [theme.breakpoints.up('md')]: {
      padding: '16px 24px 24px',
    },
  },
  '@keyframes highlightedEffect': {
    '0%': {
      background: colors.lightPurple,
    },
    '100%': {
      opacity: 1,
    },
  },
  loader: {
    position: 'absolute',
    top: 0,
    right: 0,
    zIndex: 1,
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(255,255,255,0.7)',
    cursor: 'progress',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '.5rem',
  },
  hr: {
    border: 'none',
    height: 1,
    background: colors.grey300,
    margin: '.75rem 0',
    width: '100%',
  },
  title: {
    fontSize: 14,
    fontWeight: 600,
  },
  subtitle: {
    fontSize: 14,
    fontWeight: 400,
    color: colors.grey700,
  },
  titleWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: '.5rem',
  },
  newLabel: {
    background: '#F5F0FF',
    alignSelf: 'baseline',
    fontWeight: 600,
    '& span': {
      fontSize: '11px!important',
      color: '#7B39FF',
    },
  },
}));

export default function SwitchableSection(props: Props) {
  const {
    id,
    title,
    subtitle,
    enabled,
    loading,
    children,
    chipLabel,
    classes: propClasses,
    onToggleEnable,
  } = props;

  const classes = useStyles();
  const location = useLocation();

  const isHighlighted = location.hash === `#${id || ''}`;
  return (
    <section
      className={classNames(classes.root, propClasses?.root)}
      id={id}
      data-highlighted={isHighlighted}
    >
      {loading && (
        <span className={classes.loader}>
          <CircularProgress size={20} />
        </span>
      )}
      <header className={classes.header}>
        <div className={classes.headerTitle}>
          <div>
            <div className={classes.titleWrap}>
              <Typography className={classes.title}>{title}</Typography>

              {chipLabel && (
                <Chip
                  label={chipLabel}
                  size="small"
                  className={classes.newLabel}
                />
              )}
            </div>
            {subtitle && (
              <Typography className={classes.subtitle}>{subtitle}</Typography>
            )}
          </div>
        </div>

        {onToggleEnable && (
          <IosSwitch
            checked={!!enabled}
            onChange={(e, checked) => onToggleEnable(checked)}
          />
        )}
      </header>

      <hr className={classes.hr}></hr>

      <div className={classNames(classes.content, propClasses?.content)}>
        {children}
      </div>
    </section>
  );
}
